// src/components/ProjectContent.js

import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Card, Spinner, Alert, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';

// -- Units (no fetching needed - handled in Layout.js)
import {
  selectUnits,
  selectUnitId,
} from '../../features/unitsSlice';

// -- Checkpoints
import {
  fetchCheckpoints,
  updateCheckpointDate,
  updateCheckpointTitle,
  addCheckpoint,
  deleteCheckpoint,
  sendCheckpointReminder,
} from '../../features/checkpointsSlice';

import CardIcon from '../../assets/gptw_icons/Icons_rot/activity-heart.png';
import AddCheckpointModal from '../modals/AddCheckpointModal.tsx';

const ProjectContent = () => {
  const dispatch = useDispatch();

  // --------------------------
  //  Units (already fetched in Layout.js)
  // --------------------------
  const units = useSelector(selectUnits);
  const unitsStatus = useSelector((state) => state.units.status);
  const unitId = useSelector(selectUnitId);
  const unitsError = useSelector((state) => state.units.error);

  // --------------------------
  //  Checkpoints
  // --------------------------
  const checkpoints = useSelector((state) => state.checkpoints.checkpoints);
  const checkpointsStatus = useSelector((state) => state.checkpoints.status);
  const checkpointsError = useSelector((state) => state.checkpoints.error);

  const addStatus = useSelector((state) => state.checkpoints.addStatus);
  const addError = useSelector((state) => state.checkpoints.addError);

  // Local states for updating checkpoint dates
  const [updatingCheckpoints, setUpdatingCheckpoints] = useState({});
  const [updateErrors, setUpdateErrors] = useState({});

  const [editingTitles, setEditingTitles] = useState({}); // Local editing state
  const [updatingTitles, setUpdatingTitles] = useState({}); // API update in progress
  const [titleErrors, setTitleErrors] = useState({}); // Error tracking



  // Deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checkpointToDelete, setCheckpointToDelete] = useState(null);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showDeleteErrorToast, setShowDeleteErrorToast] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const [reminderInProgress, setReminderInProgress] = useState({});
  const [showReminderSuccessToast, setShowReminderSuccessToast] = useState(false);
  const [showReminderErrorToast, setShowReminderErrorToast] = useState(false);
  const [reminderErrorMessage, setReminderErrorMessage] = useState('');

  // New state variables for adding checkpoints
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddSuccessToast, setShowAddSuccessToast] = useState(false);

  // --------------------------
  //  useEffects for data fetching
  // --------------------------

  // Units are already fetched in Layout.js - no need to fetch here

  // Fetch checkpoints (either unconditionally or when unitId is available)
  useEffect(() => {
    // If you only fetch when there's a valid unitId:
    // if (unitId) dispatch(fetchCheckpoints());
    // or, if you want *all* checkpoints unconditionally:
    dispatch(fetchCheckpoints());
  }, [dispatch /*, unitId*/]);

  // --------------------------
  //  Handlers
  // --------------------------

  const saveTitle = async (checkpointId) => {
    // Get the locally edited title
    const newTitle = editingTitles[checkpointId];

    // If there's no change, don't do anything
    const checkpoint = checkpoints.find(cp => cp.id === checkpointId);
    if (!newTitle || newTitle === checkpoint.title) {
      // Clear local edit state
      setEditingTitles(prev => {
        const updated = { ...prev };
        delete updated[checkpointId];
        return updated;
      });
      return;
    }

    // Set loading state
    setUpdatingTitles(prev => ({ ...prev, [checkpointId]: true }));
    setTitleErrors(prev => ({ ...prev, [checkpointId]: null }));

    try {
      // Save to backend
      await dispatch(updateCheckpointTitle({ checkpointId, newTitle })).unwrap();

      // Clear local edit state on success
      setEditingTitles(prev => {
        const updated = { ...prev };
        delete updated[checkpointId];
        return updated;
      });
    } catch (error) {
      setTitleErrors(prev => ({ ...prev, [checkpointId]: error }));
    } finally {
      setUpdatingTitles(prev => ({ ...prev, [checkpointId]: false }));
    }
  };

  const handleTitleBlur = (checkpointId) => {
    saveTitle(checkpointId);
  };

  const handleTitleKeyDown = (e, checkpointId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle(checkpointId);
    }
  };

  const handleTitleChange = (checkpointId, newTitle) => {
    // Just update local state, don't call API yet
    setEditingTitles(prev => ({ ...prev, [checkpointId]: newTitle }));
  };

  const handleAddCheckpoint = async (newCheckpoint) => {
    try {
      const checkpointData = {
        unitId,
        checkpointName: newCheckpoint.name,
        endsAt: newCheckpoint.date,
        checkpointType: newCheckpoint.type
      };

      // Add type-specific data to the checkpoint creation
      if (newCheckpoint.type === 'questions' && newCheckpoint.questionId) {
        checkpointData.questionId = newCheckpoint.questionId;
      } else if (newCheckpoint.type === 'actionareas' && newCheckpoint.actionAreaId) {
        checkpointData.actionAreaId = newCheckpoint.actionAreaId;
      }

      await dispatch(
        addCheckpoint(checkpointData)
      ).unwrap();

      setShowAddModal(false);
      setShowAddSuccessToast(true);
    } catch (error) {
      console.error('Failed to add checkpoint:', error);
    }
  };

  const handleDateChange = async (checkpointId, newDate) => {
    setUpdatingCheckpoints((prev) => ({ ...prev, [checkpointId]: true }));
    setUpdateErrors((prev) => ({ ...prev, [checkpointId]: null }));

    try {
      await dispatch(
        updateCheckpointDate({
          unitId,
          checkpointId,
          newDate,
        })
      ).unwrap();
    } catch (error) {
      setUpdateErrors((prev) => ({ ...prev, [checkpointId]: error }));
    } finally {
      setUpdatingCheckpoints((prev) => ({ ...prev, [checkpointId]: false }));
    }
  };

  const handleDeleteClick = (checkpoint) => {
    setCheckpointToDelete(checkpoint);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!checkpointToDelete) return;
    dispatch(deleteCheckpoint(checkpointToDelete.id))
      .unwrap()
      .then(() => {
        setShowDeleteSuccessToast(true);
      })
      .catch((error) => {
        setDeleteErrorMessage(error);
        setShowDeleteErrorToast(true);
      })
      .finally(() => {
        setShowDeleteModal(false);
        setCheckpointToDelete(null);
      });
  };

  return (
    <>

      <h3 className="mt-4"><b>Projektplan</b></h3>
      <p className="mb-3">
        Bitte wähle die Checkpoints Deines Projekts aus und lege Fristen fest bis wann diese bearbeitet werden sollen.
      </p>

      <Row className="mb-3">
        <Col sm={6} className="mb-3">
          <p>
            Die Checkpoints gestalten den zeitlichen Plan des Follow up Prozesses.<br />
            Ein üblicher Ablauf ist wie folgt:
          </p>
          <ul className="mb-4">
            <li>Kommunikation der Ergebnisse</li>
            <li>Durchführung Auswertungsgespräch</li>
            <li>Dokumentation Handlungsfelder und Maßnahmen</li>
            <li>Rückmeldung Maßnahmenumsetzung (dieser Checkpoint kann in längeren Projekten mehrfach genutzt werden. Z. B. gibt es jeden Monat einen Checkpoint zum Stand der Maßnahmenumsetzung)</li>
            <li>Ergebnisse dokumentieren</li>
          </ul>
        </Col>

        <Col sm={6} className="d-flex mb-3">
          <Card className="flex-fill">
            <Card.Body>
              <img src={CardIcon} alt="Card icon" className="mb-2" />
              <Form>
                {/* Checkpoints Count */}
                <Form.Group controlId="checkpointsCount">
                  <Form.Label>Checkpoints</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    value={checkpoints.length}
                    placeholder="Anzahl der Checkpoints"
                  />
                </Form.Group>
                <hr />


                {/* 2) Units Error State */}
                {unitsStatus === 'failed' && (
                  <Alert variant="danger">
                    Fehler beim Laden der Einheiten: {unitsError}
                  </Alert>
                )}

                {/* Combined Loading Spinner */}
                {(unitsStatus === 'loading' || checkpointsStatus === 'loading') && (
                  <div className="d-flex justify-content-center my-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Lädt...</span>
                    </Spinner>
                  </div>
                )}


                {/* 4) Checkpoints Error State */}
                {checkpointsStatus === 'failed' && (
                  <Alert variant="danger">
                    Fehler beim Laden der Checkpoints: {checkpointsError}
                  </Alert>
                )}

                {/* 5) Display Checkpoints */}
                {checkpointsStatus === 'succeeded' && checkpoints.length > 0 && (
                  <>
                    {checkpoints.map((checkpoint) => (
                      <div key={checkpoint.id}>
                        <Form.Group controlId={`checkpoint-${checkpoint.id}`}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Control
                              type="text"
                              value={editingTitles[checkpoint.id] !== undefined ?
                                editingTitles[checkpoint.id] :
                                checkpoint.title}
                              placeholder="Checkpoint Titel"
                              className="fw-medium"
                              onChange={(e) => handleTitleChange(checkpoint.id, e.target.value)}
                              onBlur={() => handleTitleBlur(checkpoint.id)}
                              onKeyDown={(e) => handleTitleKeyDown(e, checkpoint.id)}
                              isInvalid={!!titleErrors[checkpoint.id]}
                              disabled={updatingTitles[checkpoint.id]}
                            />
                            {updatingTitles[checkpoint.id] && (
                              <Spinner
                                animation="border"
                                size="sm"
                                role="status"
                              >
                                <span className="visually-hidden">Speichern...</span>
                              </Spinner>
                            )}
                          </div>

                          {/* Rest of your checkpoint rendering code */}
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="date"
                              value={
                                checkpoint.dueDate
                                  ? checkpoint.dueDate.split('T')[0]
                                  : ''
                              }
                              placeholder="Datum des Checkpoints"
                              className="me-2"
                              onChange={(e) =>
                                handleDateChange(checkpoint.id, e.target.value)
                              }
                              isInvalid={!!updateErrors[checkpoint.id]}
                            />
                            {updatingCheckpoints[checkpoint.id] && (
                              <Spinner
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                              >
                                <span className="visually-hidden">Lädt...</span>
                              </Spinner>
                            )}
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleDeleteClick(checkpoint)}
                            >
                              Löschen
                            </Button>
                          </div>

                          {updateErrors[checkpoint.id] && (
                            <Form.Control.Feedback type="invalid">
                              {updateErrors[checkpoint.id]}
                            </Form.Control.Feedback>
                          )}

                          {titleErrors[checkpoint.id] && (
                            <div className="text-danger small">
                              {titleErrors[checkpoint.id]}
                            </div>
                          )}
                        </Form.Group>
                        <hr />
                      </div>
                    ))}
                  </>
                )}

                {/* If there are no checkpoints */}
                {checkpointsStatus === 'succeeded' && checkpoints.length === 0 && (
                  <p>Keine Checkpoints verfügbar.</p>
                )}

                {/* Add Checkpoint Button and Feedback */}
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="primary"
                    onClick={() => setShowAddModal(true)}
                    disabled={addStatus === 'loading'}
                  >
                    Checkpoint hinzufügen
                  </Button>
                </div>

                {/* Replace the Add Checkpoint Modal with the new component */}
                <AddCheckpointModal
                  show={showAddModal}
                  onHide={() => setShowAddModal(false)}
                  onAdd={handleAddCheckpoint}
                  checkpointsCount={checkpoints.length}
                  addStatus={addStatus}
                  addError={addError}
                />

                {addStatus === 'failed' && (
                  <Alert variant="danger" className="mt-2">
                    Fehler beim Hinzufügen des Checkpoints: {addError}
                  </Alert>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Löschvorgang bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sind Sie sicher, dass Sie den Checkpoint{' '}
          <strong>{checkpointToDelete?.title}</strong> löschen möchten?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleConfirmDelete}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1060,
        }}
        className="p-3"
      >
        {/* Existing toasts */}
        {/* Success Toast */}
        <Toast
          onClose={() => setShowDeleteSuccessToast(false)}
          show={showDeleteSuccessToast}
          bg="success"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Erfolg</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            Checkpoint erfolgreich gelöscht!
          </Toast.Body>
        </Toast>

        {/* Error Toast */}
        {showDeleteErrorToast && (
          <Toast
            onClose={() => setShowDeleteErrorToast(false)}
            show={showDeleteErrorToast}
            bg="danger"
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">Fehler</strong>
              <small>Jetzt</small>
              <Button
                type="button"
                variant="close"
                aria-label="Close"
                onClick={() => setShowDeleteErrorToast(false)}
              />
            </Toast.Header>
            <Toast.Body className="text-white">{deleteErrorMessage}</Toast.Body>
          </Toast>
        )}

        {/* New Reminder Success Toast */}
        <Toast
          onClose={() => setShowReminderSuccessToast(false)}
          show={showReminderSuccessToast}
          bg="success"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Erfolg</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            Erinnerung erfolgreich gesendet!
          </Toast.Body>
        </Toast>

        {/* New Reminder Error Toast */}
        {showReminderErrorToast && (
          <Toast
            onClose={() => setShowReminderErrorToast(false)}
            show={showReminderErrorToast}
            bg="danger"
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">Fehler</strong>
              <small>Jetzt</small>
              <Button
                type="button"
                variant="close"
                aria-label="Close"
                onClick={() => setShowReminderErrorToast(false)}
              />
            </Toast.Header>
            <Toast.Body className="text-white">{reminderErrorMessage}</Toast.Body>
          </Toast>
        )}

        {/* Add success toast */}
        <Toast
          onClose={() => setShowAddSuccessToast(false)}
          show={showAddSuccessToast}
          bg="success"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Erfolg</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            Checkpoint erfolgreich hinzugefügt!
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ProjectContent;